import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import './StaffView.css';

function StaffView({ staff, staffRsvps }) {
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedGuests, setExpandedGuests] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [note, setNote] = useState('');
  const [localStaff, setLocalStaff] = useState([]);
  const [editMode, setEditMode] = useState('add'); // 'add' or 'edit'
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  
  // Status message handling
  const [showMessage, setShowMessage] = useState(false);
  const [messageType, setMessageType] = useState('');
  const [messageText, setMessageText] = useState('');
  
  // Initialize local staff when props change
  useEffect(() => {
    setLocalStaff([...staff]);
  }, [staff]);
  
  // Show message helper
  const displayMessage = (type, text) => {
    setMessageType(type);
    setMessageText(text);
    setShowMessage(true);
    
    // Auto-hide message after 3 seconds
    setTimeout(() => {
      setShowMessage(false);
    }, 3000);
  };
  
  // Filter and sort the staff data
  const filteredStaff = localStaff
    .filter(staffMember => {
      // Filter by search term
      const matchesSearch = staffMember.name && staffMember.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filter by status
      if (statusFilter === 'all') return matchesSearch;
      
      const rsvp = staffRsvps.find(r => r.username === staffMember.username || r.staffUsername === staffMember.username);
      const hasResponded = rsvp && (rsvp.status === 'confirmed' || rsvp.completedSteps?.includes('confirmation'));
      
      if (statusFilter === 'responded' && hasResponded) return matchesSearch;
      if (statusFilter === 'not-responded' && !hasResponded) return matchesSearch;
      
      return false;
    })
    .sort((a, b) => {
      // Sort by selected field
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      // Handle special case for status
      if (sortField === 'status') {
        const aRsvp = staffRsvps.find(r => r.username === a.username || r.staffUsername === a.username);
        const bRsvp = staffRsvps.find(r => r.username === b.username || r.staffUsername === b.username);
        
        aValue = aRsvp && (aRsvp.status === 'confirmed' || aRsvp.completedSteps?.includes('confirmation')) ? 1 : 0;
        bValue = bRsvp && (bRsvp.status === 'confirmed' || bRsvp.completedSteps?.includes('confirmation')) ? 1 : 0;
      }
      
      // Handle special case for guest count
      if (sortField === 'guestCount') {
        const aRsvp = staffRsvps.find(r => r.username === a.username || r.staffUsername === a.username);
        const bRsvp = staffRsvps.find(r => r.username === b.username || r.staffUsername === b.username);
        
        aValue = aRsvp ? (aRsvp.guestCount || 0) : 0;
        bValue = bRsvp ? (bRsvp.guestCount || 0) : 0;
      }
      
      // Compare values based on sort direction
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  
  // Handle sort header click
  const handleSort = (field) => {
    if (field === sortField) {
      // Toggle sort direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Handle guest details toggle
  const handleGuestDetails = (staffUsername, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (expandedGuests === staffUsername) {
      setExpandedGuests(null);
    } else {
      setExpandedGuests(staffUsername);
    }
  };
  
  // Start adding a new note
  const startAddingNote = (staffUsername, e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingStaff(staffUsername);
    setNote('');
    setEditMode('add');
    setEditingNoteIndex(null);
  };
  
  // Start editing an existing note
  const startEditingNote = (staffUsername, noteIndex, noteText, e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingStaff(staffUsername);
    setNote(noteText);
    setEditMode('edit');
    setEditingNoteIndex(noteIndex);
  };
  
  // Cancel editing
  const cancelEditing = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingStaff(null);
    setNote('');
    setEditMode('add');
    setEditingNoteIndex(null);
  };
  
  // Update note text field
  const updateNote = (e) => {
    setNote(e.target.value);
  };
  
  // Save new note to database and update local state
  const saveNewNote = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!editingStaff || !note.trim()) {
      return;
    }
    
    try {
      // Update Firestore
      const staffRef = doc(db, "staff", editingStaff);
      await updateDoc(staffRef, {
        notes: arrayUnion(note.trim())
      });
      
      // Update local state
      const updatedStaff = localStaff.map(staffMember => {
        if (staffMember.username === editingStaff) {
          const existingNotes = staffMember.notes || [];
          return {
            ...staffMember,
            notes: [...existingNotes, note.trim()]
          };
        }
        return staffMember;
      });
      
      // Update state
      setLocalStaff(updatedStaff);
      setEditingStaff(null);
      setNote('');
      setEditMode('add');
      
      // Show success message
      displayMessage('success', 'Note added successfully!');
      
    } catch (error) {
      console.error("Error adding note:", error);
      displayMessage('error', 'Error adding note. Please try again.');
    }
  };
  
  // Update existing note
  const updateExistingNote = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!editingStaff || !note.trim() || editingNoteIndex === null) {
      return;
    }
    
    try {
      // Get the staff member
      const staffMember = localStaff.find(s => s.username === editingStaff);
      if (!staffMember) {
        throw new Error("Staff member not found");
      }
      
      // Create updated notes array
      const updatedNotes = [...(staffMember.notes || [])];
      updatedNotes[editingNoteIndex] = note.trim();
      
      // Update Firestore
      const staffRef = doc(db, "staff", editingStaff);
      await updateDoc(staffRef, {
        notes: updatedNotes
      });
      
      // Update local state
      const updatedStaff = localStaff.map(staffMember => {
        if (staffMember.username === editingStaff) {
          return {
            ...staffMember,
            notes: updatedNotes
          };
        }
        return staffMember;
      });
      
      // Update state
      setLocalStaff(updatedStaff);
      setEditingStaff(null);
      setNote('');
      setEditMode('add');
      setEditingNoteIndex(null);
      
      // Show success message
      displayMessage('success', 'Note updated successfully!');
      
    } catch (error) {
      console.error("Error updating note:", error);
      displayMessage('error', 'Error updating note. Please try again.');
    }
  };

  // Delete note
  const deleteNote = async (staffUsername, noteIndex, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!staffUsername || noteIndex === null) {
      return;
    }
    
    try {
      // Get the staff member
      const staffMember = localStaff.find(s => s.username === staffUsername);
      if (!staffMember) {
        throw new Error("Staff member not found");
      }
      
      // Create updated notes array (filter out the one we want to delete)
      const updatedNotes = staffMember.notes.filter((_, index) => index !== noteIndex);
      
      // Update Firestore
      const staffRef = doc(db, "staff", staffUsername);
      await updateDoc(staffRef, {
        notes: updatedNotes
      });
      
      // Update local state
      const updatedStaff = localStaff.map(staffMember => {
        if (staffMember.username === staffUsername) {
          return {
            ...staffMember,
            notes: updatedNotes
          };
        }
        return staffMember;
      });
      
      // Update state
      setLocalStaff(updatedStaff);
      
      // Show success message
      displayMessage('success', 'Note deleted successfully!');
      
    } catch (error) {
      console.error("Error deleting note:", error);
      displayMessage('error', 'Error deleting note. Please try again.');
    }
  };
  
  return (
    <div className="table-view-container">
      <h1>Staff RSVPs</h1>
      
      <div className="filters-container">
        <div className="search-box">
          <input 
            type="text"
            placeholder="Search by name..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-dropdown">
          <label>Status:</label>
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="responded">Responded</option>
            <option value="not-responded">Not Responded</option>
          </select>
        </div>
        
        <div className="count-badge">
          {filteredStaff.length} staff members
        </div>
      </div>
      
      <div className="responsive-table-container">
        {filteredStaff.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th 
                  className={sortField === 'name' ? `sorted-${sortDirection}` : ''}
                  onClick={() => handleSort('name')}
                >
                  Name
                </th>
                <th 
                  className={sortField === 'department' ? `sorted-${sortDirection}` : ''}
                  onClick={() => handleSort('department')}
                >
                  Department
                </th>
                <th 
                  className={sortField === 'status' ? `sorted-${sortDirection}` : ''}
                  onClick={() => handleSort('status')}
                >
                  Status
                </th>
                <th 
                  className={sortField === 'guestCount' ? `sorted-${sortDirection}` : ''}
                  onClick={() => handleSort('guestCount')}
                >
                  Guests
                </th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStaff.map(staffMember => {
                const rsvp = staffRsvps.find(r => r.username === staffMember.username || r.staffUsername === staffMember.username);
                const hasResponded = rsvp && (rsvp.status === 'confirmed' || rsvp.completedSteps?.includes('confirmation'));
                
                return (
                  <tr 
                    key={staffMember.username}
                    className={hasResponded ? 'confirmed-row' : 'not-confirmed-row'}
                  >
                    <td>{staffMember.name}</td>
                    <td>{staffMember.department || 'N/A'}</td>
                    <td>
                      {hasResponded ? (
                        <span className="status-badge confirmed">Confirmed</span>
                      ) : (
                        <span className="status-badge not-confirmed">Not Confirmed</span>
                      )}
                    </td>
                    <td className="guest-info">
                      {hasResponded ? (
                        <>
                          <span className="guest-count">{rsvp.guestCount || 0}</span>
                          {rsvp.guests && rsvp.guests.length > 0 && (
                            <>
                              <button 
                                type="button"
                                className="guest-details-button"
                                onClick={(e) => handleGuestDetails(staffMember.username, e)}
                              >
                                Details
                              </button>
                              
                              {expandedGuests === staffMember.username && (
                                <div className="guest-details-expanded">
                                  {rsvp.guests.map((guest, index) => (
                                    <div key={index} className="guest-item-expanded">
                                      <div className="guest-name">{guest.name}</div>
                                      <div className="guest-relationship">{guest.relationship}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td>
                      {editingStaff === staffMember.username ? (
                        <div className="edit-field">
                          <textarea
                            value={note}
                            onChange={updateNote}
                            placeholder="Enter note..."
                          ></textarea>
                          <div className="edit-actions">
                            <button 
                              type="button" 
                              className="edit-save"
                              onClick={editMode === 'add' ? saveNewNote : updateExistingNote}
                            >
                              {editMode === 'add' ? 'Add' : 'Update'}
                            </button>
                            <button 
                              type="button" 
                              className="edit-cancel"
                              onClick={cancelEditing}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {staffMember.notes && staffMember.notes.length > 0 ? (
                            <ul className="notes-list">
                              {staffMember.notes.map((noteText, index) => (
                                <li key={index} className="note-item">
                                  <div className="note-text">{noteText}</div>
                                  <div className="note-actions">
                                    <button 
                                      type="button"
                                      className="edit-note-button"
                                      onClick={(e) => startEditingNote(staffMember.username, index, noteText, e)}
                                    >
                                      Edit
                                    </button>
                                    <button 
                                      type="button"
                                      className="delete-note-button"
                                      onClick={(e) => deleteNote(staffMember.username, index, e)}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            'No notes yet'
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      {editingStaff !== staffMember.username && (
                        <button 
                          type="button"
                          className="edit-button"
                          onClick={(e) => startAddingNote(staffMember.username, e)}
                        >
                          Add Note
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="no-results">No staff members match your filter criteria.</p>
        )}
      </div>
      
      {showMessage && (
        <div className={`status-message ${messageType}`}>
          {messageText}
        </div>
      )}
    </div>
  );
}

export default StaffView;